import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Ticket, CheckCircle2 } from "lucide-react";
import { api } from "@/context/AuthContext";
import { toast } from "sonner";

export default function PromoPopup() {
  const [show, setShow] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState("");

  useEffect(() => {
    if (localStorage.getItem("tbl_promo_seen")) return;
    const t = setTimeout(() => setShow(true), 2500);
    return () => clearTimeout(t);
  }, []);

  const close = () => {
    localStorage.setItem("tbl_promo_seen", "1");
    setShow(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/promo-signups", { email });
      setCode(data.code);
      localStorage.setItem("tbl_promo_seen", "1");
      toast.success("Code unlocked — check your email.");
    } catch {
      toast.error("Couldn't save that — try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div data-testid="promo-modal" className="fixed inset-0 z-[75] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={close}>
          <motion.div
            className="relative w-full max-w-md bg-court-surface border border-court-amber/30 rounded-2xl p-8 shadow-2xl overflow-hidden"
            initial={{ y: 40, opacity: 0, scale: 0.96 }} animate={{ y: 0, opacity: 1, scale: 1 }} exit={{ y: 20, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute inset-x-0 top-0 h-1 bg-court-amber" />
            <button data-testid="promo-close-btn" onClick={close} className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors">
              <X size={20} />
            </button>
            <div className="flex items-center gap-2 mb-4">
              <Ticket size={15} className="text-court-amber" />
              <span className="font-mono text-xs uppercase tracking-widest text-court-amber font-bold">New Customer Offer</span>
            </div>
            {!code ? (
              <>
                <h3 className="font-display font-black uppercase tracking-tight text-2xl sm:text-3xl leading-[0.95]">
                  Get <span className="text-court-amber">$10 off</span> your first session
                </h3>
                <p className="text-sm text-zinc-400 mt-3 leading-relaxed">
                  Drop your email and we'll send your code instantly — mention it when you book and Theo takes $10 off your first lesson.
                </p>
                <form onSubmit={submit} className="mt-6 space-y-3">
                  <input data-testid="promo-email-input" type="email" className="input-dark" placeholder="Your email address"
                    value={email} onChange={(e) => setEmail(e.target.value)} required />
                  <button data-testid="promo-submit-btn" type="submit" disabled={busy}
                    className="btn-amber w-full bg-court-amber text-court-void font-display font-extrabold uppercase tracking-wide text-lg py-3 rounded-lg disabled:opacity-60">
                    {busy ? "Unlocking..." : "Unlock My $10 Off"}
                  </button>
                </form>
                <p className="mt-4 text-[11px] font-mono uppercase tracking-wider text-zinc-600">One per athlete • Applied when you book</p>
              </>
            ) : (
              <div data-testid="promo-success" className="text-center py-2">
                <CheckCircle2 size={36} className="text-emerald-400 mx-auto" />
                <h3 className="font-display font-black uppercase tracking-tight text-2xl sm:text-3xl mt-4">You're in.</h3>
                <p className="text-sm text-zinc-400 mt-2">Your code is on its way to your inbox — and saved here:</p>
                <div className="mt-5 font-display font-black text-4xl tracking-widest text-court-amber border border-dashed border-court-amber/50 rounded-xl py-4" data-testid="promo-code-display">
                  {code}
                </div>
                <p className="text-xs text-zinc-500 mt-4">Mention it when you book, or enter it in the booking form's promo field.</p>
                <button data-testid="promo-done-btn" onClick={close}
                  className="btn-amber mt-6 w-full bg-court-amber text-court-void font-display font-extrabold uppercase tracking-wide text-lg py-3 rounded-lg">
                  Start Browsing Sessions
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
